import { Injectable, signal, computed } from '@angular/core';

export interface ContactarUtenteMedication {
  id: string;
  name: string;
}

export interface ContactarUtenteContext {
  accountId: string;
  patientName: string;
  patientPhone: string | null | undefined;
  primaryContactId?: string | null;
  medications?: ContactarUtenteMedication[];
}

/**
 * Manages the "Contactar utente" side panel state at the app level,
 * so the panel persists across route changes (like the Ana assistant).
 */
@Injectable({ providedIn: 'root' })
export class ContactarUtentePanelService {
  readonly isOpen = signal<boolean>(false);
  readonly isMinimized = signal<boolean>(false);
  readonly context = signal<ContactarUtenteContext | null>(null);

  /** Incremented each time a contact is successfully saved. Pages can watch this to refresh. */
  readonly savedVersion = signal<number>(0);

  readonly accountId = computed(() => this.context()?.accountId ?? '');
  readonly patientName = computed(() => this.context()?.patientName ?? '');
  readonly patientPhone = computed(() => this.context()?.patientPhone ?? null);
  readonly primaryContactId = computed(() => this.context()?.primaryContactId ?? null);
  readonly medications = computed(() => this.context()?.medications ?? []);

  open(ctx: ContactarUtenteContext): void {
    this.context.set(ctx);
    this.isMinimized.set(false);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.isMinimized.set(false);
    this.context.set(null);
  }

  /** Collapse the panel to a floating tab — form state preserved. */
  minimize(): void {
    this.isMinimized.set(true);
  }

  /** Restore from minimized state. */
  restore(): void {
    this.isMinimized.set(false);
  }

  /** Called by the panel after a successful save. */
  notifySaved(): void {
    this.savedVersion.update(v => v + 1);
  }

}
